import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import {
  CreatePushBroadcastMutation,
  CreatePushBroadcastMutationVariables,
  DeletePushBroadcastMutation,
  DeletePushBroadcastMutationVariables,
  DuplicatePushBroadcastMutation,
  DuplicatePushBroadcastMutationVariables,
  GetPushBroadcastQuery,
  GetPushBroadcastQueryVariables,
  PreviewPushBroadcastAudienceMutation,
  PreviewPushBroadcastAudienceMutationVariables,
  PushBroadcastStatus,
  UpdatePushBroadcastMutation,
  UpdatePushBroadcastMutationVariables,
} from '../../../../../../__gqltypes__';
import { LoadingLogo } from '../../../../devtools/components/Modal';
import {
  CREATE_PUSH_BROADCAST,
  DELETE_PUSH_BROADCAST,
  DUPLICATE_PUSH_BROADCAST,
  GET_PUSH_BROADCAST,
  PREVIEW_PUSH_BROADCAST_AUDIENCE,
  UPDATE_PUSH_BROADCAST,
} from '../graphql';
import { usePushPeople } from '../usePushPeople';
import {
  emptyPushBroadcastDraft,
  formatPushUtc,
  isPushContentDirty,
  isPushFiltersDirty,
  NEW_PUSH_BROADCAST_ID,
  PUSH_BROADCAST_ACTIVE_STATUSES,
  PUSH_BROADCAST_BASE_URL,
  PUSH_BROADCAST_FINISHED_STATUSES,
  PUSH_BROADCAST_STATUS_TONE,
  pushBroadcastErrorIssues,
  PushBroadcastData,
  PushBroadcastDraft,
  PushBroadcastOptions,
  pushBroadcastToDraft,
  pushDraftToInput,
  PushIssue,
  pushOverrideLanguages,
  pushPersonLabel,
  sourceLanguageOf,
} from '../utils';
import { PushAudience } from './PushAudience';
import { PushBroadcastEditor } from './PushBroadcastEditor';
import { PushIssueList } from './PushIssueList';
import { PushPreview } from './PushPreview';
import { PushProgress } from './PushProgress';
import { SolidBadge } from '../../../../../../utils/components/SolidBadge';
import { PushSchedulePanel } from './PushSchedulePanel';
import { PushSendPanel } from './PushSendPanel';
import { PushTranslationsXlsx } from './PushTranslationsXlsx';

type Props = {
  // NEW_PUSH_BROADCAST_ID for an editor not saved yet. The parent remounts this component when it changes.
  broadcastId: string;
  options: PushBroadcastOptions;
  onCreated: (broadcast: PushBroadcastData) => void;
  onDeleted: () => void;
  // A copy was made: the parent opens it.
  onDuplicated: (copy: PushBroadcastData) => void;
};

// How often a broadcast being sent is re-read.
const SENDING_POLL_MS = 3000;
// A scheduled one, to see it start, or its overdue flag when its task was lost.
const SCHEDULED_POLL_MS = 30000;

/** Why the editor is read-only, in the operator's words; null when it can be edited. */
function readOnlyReason(broadcast: PushBroadcastData | null): string | null {
  if (!broadcast || broadcast.status === PushBroadcastStatus.DRAFT) {
    return null;
  }
  if (broadcast.status === PushBroadcastStatus.SCHEDULED) {
    return 'Scheduled: edits are blocked, so what was checked is what is sent. Use "Back to draft (to edit)" in the Scheduled panel to change it.';
  }
  return 'A started broadcast is final: its texts and audience can no longer change. Duplicate it to send a changed copy.';
}

/**
 * One broadcast: the editor (the push, its text with the translation spreadsheet, the audience with its count and the
 * send) with the rendered preview beside it, and the delivery progress once started.
 */
export function PushBroadcastDetail({ broadcastId, options, onCreated, onDeleted, onDuplicated }: Props): JSX.Element {
  const isNew = broadcastId === NEW_PUSH_BROADCAST_ID;
  const sourceLanguage = sourceLanguageOf(options.pushBroadcastLanguages);
  const defaultDays = options.pushBroadcastLimits.defaultDaysSinceLastOnline;

  const [latest, setBroadcast] = useState<PushBroadcastData | null>(null);
  const isActive = !!latest && PUSH_BROADCAST_ACTIVE_STATUSES.includes(latest.status);
  const isScheduled = latest?.status === PushBroadcastStatus.SCHEDULED;
  const { data, loading, error } = useQuery<GetPushBroadcastQuery, GetPushBroadcastQueryVariables>(GET_PUSH_BROADCAST, {
    variables: { id: broadcastId },
    skip: isNew,
    fetchPolicy: 'network-only',
    // Batches by status while it sends; a draft is never reloaded under the editor.
    pollInterval: isActive ? SENDING_POLL_MS : isScheduled ? SCHEDULED_POLL_MS : 0,
  });
  // Null until the broadcast is loaded; then only our own writes reset it, never a reload of the server state.
  const [draft, setDraft] = useState<PushBroadcastDraft | null>(() =>
    isNew ? emptyPushBroadcastDraft(sourceLanguage, defaultDays) : null
  );
  const [issues, setIssues] = useState<PushIssue[]>([]);

  useEffect(() => {
    const loaded = data?.pushBroadcast;
    if (loaded) {
      setBroadcast(loaded);
      setDraft((previous) => previous ?? pushBroadcastToDraft(loaded));
    }
  }, [data]);

  const [createBroadcast, { loading: creating }] = useMutation<
    CreatePushBroadcastMutation,
    CreatePushBroadcastMutationVariables
  >(CREATE_PUSH_BROADCAST);
  const [updateBroadcast, { loading: updating }] = useMutation<
    UpdatePushBroadcastMutation,
    UpdatePushBroadcastMutationVariables
  >(UPDATE_PUSH_BROADCAST);
  const [deleteBroadcast, { loading: deleting }] = useMutation<
    DeletePushBroadcastMutation,
    DeletePushBroadcastMutationVariables
  >(DELETE_PUSH_BROADCAST);
  const [countAudience, { loading: counting }] = useMutation<
    PreviewPushBroadcastAudienceMutation,
    PreviewPushBroadcastAudienceMutationVariables
  >(PREVIEW_PUSH_BROADCAST_AUDIENCE);
  const [duplicateBroadcast, { loading: duplicating }] = useMutation<
    DuplicatePushBroadcastMutation,
    DuplicatePushBroadcastMutationVariables
  >(DUPLICATE_PUSH_BROADCAST);

  // The query result until the effect above copied it: no "does not exist" flash in between.
  const broadcast = latest ?? data?.pushBroadcast ?? null;

  const people = usePushPeople([
    broadcast?.createdBy,
    broadcast?.updatedBy,
    broadcast?.startedBy,
    broadcast?.cancelledBy,
    broadcast?.audience?.computedBy,
  ]);

  if (!isNew && !broadcast) {
    return (
      <>
        <LoadingLogo show={loading} />
        {error ? <Alert variant="danger">Failed to load the broadcast: {error.message}</Alert> : null}
        {!loading && !error ? (
          <Alert variant="warning">
            This broadcast does not exist (it may have been deleted).{' '}
            <Link to={PUSH_BROADCAST_BASE_URL}>Back to the list</Link>
          </Alert>
        ) : null}
      </>
    );
  }

  const saved = broadcast ? pushBroadcastToDraft(broadcast) : emptyPushBroadcastDraft(sourceLanguage, defaultDays);
  const editing = draft ?? saved;
  const lockReason = readOnlyReason(broadcast);
  // A read-only broadcast always shows the server state.
  const shown = lockReason ? saved : editing;
  const isContentDirty = !lockReason && isPushContentDirty(editing, saved);
  const isFiltersDirty = !lockReason && isPushFiltersDirty(editing, saved);
  const isDirty = isNew || isContentDirty || isFiltersDirty;
  const busy = creating || updating || deleting || counting || duplicating;
  const isStarted =
    !!broadcast && ![PushBroadcastStatus.DRAFT, PushBroadcastStatus.SCHEDULED].includes(broadcast.status);
  const isFinished = !!broadcast && PUSH_BROADCAST_FINISHED_STATUSES.includes(broadcast.status);

  const applySaved = (updated: PushBroadcastData) => {
    setBroadcast(updated);
    setDraft(pushBroadcastToDraft(updated));
  };

  // Saves the editor; the server reports every error at once, all of them shown.
  const save = async (): Promise<PushBroadcastData | null> => {
    setIssues([]);
    try {
      if (!broadcast) {
        const result = await createBroadcast({ variables: { input: pushDraftToInput(editing) } });
        const created = result.data?.createPushBroadcast ?? null;
        if (created) {
          onCreated(created);
        }
        return created;
      }
      const result = await updateBroadcast({
        variables: { id: broadcast.id, expectedVersion: broadcast.revision, input: pushDraftToInput(editing) },
      });
      const updated = result.data?.updatePushBroadcast ?? null;
      if (updated) {
        applySaved(updated);
      }
      return updated;
    } catch (saveError) {
      setIssues(pushBroadcastErrorIssues(saveError));
      return null;
    }
  };

  // Only on the button: unsaved edits are saved first, since the server counts the saved criteria.
  const count = async () => {
    const target = isDirty ? await save() : broadcast;
    if (!target) {
      return;
    }
    setIssues([]);
    try {
      const result = await countAudience({ variables: { id: target.id } });
      if (result.data) {
        applySaved(result.data.previewPushBroadcastAudience);
      }
    } catch (countError) {
      setIssues(pushBroadcastErrorIssues(countError));
    }
  };

  const duplicate = async () => {
    if (
      !broadcast ||
      (isDirty && !window.confirm('Duplicate copies the saved broadcast: your unsaved edits are not copied.'))
    ) {
      return;
    }
    setIssues([]);
    try {
      const result = await duplicateBroadcast({ variables: { id: broadcast.id } });
      if (result.data) {
        onDuplicated(result.data.duplicatePushBroadcast);
      }
    } catch (duplicateError) {
      setIssues(pushBroadcastErrorIssues(duplicateError));
    }
  };

  const remove = async () => {
    if (!broadcast || !window.confirm(`Delete the draft "${broadcast.name}"? This cannot be undone.`)) {
      return;
    }
    setIssues([]);
    try {
      await deleteBroadcast({ variables: { id: broadcast.id } });
      onDeleted();
    } catch (deleteError) {
      setIssues(pushBroadcastErrorIssues(deleteError));
    }
  };

  return (
    <>
      <div className="mb-2">
        <Link to={PUSH_BROADCAST_BASE_URL}>← All push notifications</Link>
      </div>
      <Card className="mb-3">
        <Card.Body className="py-2">
          <div className="d-flex flex-wrap align-items-center">
            <h4 className="mb-0 mr-2">{broadcast ? broadcast.name : 'New push notification'}</h4>
            {broadcast ? (
              <SolidBadge tone={PUSH_BROADCAST_STATUS_TONE[broadcast.status]} className="mr-3">
                {broadcast.status}
              </SolidBadge>
            ) : null}
            {!lockReason ? (
              <>
                <Button size="sm" className="mr-2" disabled={busy || !isDirty} onClick={save}>
                  {busy ? <Spinner animation="border" size="sm" className="mr-1" /> : null}
                  {broadcast ? 'Save draft' : 'Create draft'}
                </Button>
                {broadcast ? (
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="mr-2"
                    disabled={busy || !isDirty}
                    onClick={() => {
                      setDraft(saved);
                      setIssues([]);
                    }}
                  >
                    Discard changes
                  </Button>
                ) : null}
                {isDirty && broadcast ? <span className="text-warning mr-3">Unsaved changes</span> : null}
                {/* Save / Discard are off with nothing to save: say so beside them. */}
                {!isDirty && !busy ? <small className="text-muted mr-3">Nothing to save yet</small> : null}
              </>
            ) : null}
            {broadcast ? (
              <span className="ml-auto">
                <Button
                  size="sm"
                  variant={isFinished ? 'primary' : 'outline-primary'}
                  className="mr-2"
                  disabled={busy}
                  onClick={duplicate}
                  title="A new draft with the same texts, destination and category; the audience is not copied"
                >
                  {duplicating ? <Spinner animation="border" size="sm" className="mr-1" /> : null}
                  Duplicate
                </Button>
                {broadcast.status === PushBroadcastStatus.DRAFT ? (
                  <Button size="sm" variant="outline-danger" disabled={busy} onClick={remove}>
                    Delete draft
                  </Button>
                ) : null}
              </span>
            ) : null}
          </div>
          {broadcast ? (
            <small className="text-muted d-block mt-1">
              Created {formatPushUtc(broadcast.createdAt)} by {pushPersonLabel(broadcast.createdBy, people)}
              {broadcast.updatedAt
                ? ` · saved ${formatPushUtc(broadcast.updatedAt)} by ${pushPersonLabel(broadcast.updatedBy, people)}`
                : ''}
              {broadcast.startedAt
                ? ` · started ${formatPushUtc(broadcast.startedAt)} by ${pushPersonLabel(broadcast.startedBy, people)}`
                : ''}
              {broadcast.cancelledAt
                ? ` · cancelled ${formatPushUtc(broadcast.cancelledAt)} by ${pushPersonLabel(
                    broadcast.cancelledBy,
                    people
                  )}`
                : ''}
              {broadcast.completedAt ? ` · finished ${formatPushUtc(broadcast.completedAt)}` : ''}
            </small>
          ) : null}
        </Card.Body>
      </Card>

      {broadcast?.duplicatedFromId && broadcast.status === PushBroadcastStatus.DRAFT ? (
        <Alert variant="info">
          A copy of <Link to={`${PUSH_BROADCAST_BASE_URL}/${broadcast.duplicatedFromId}`}>the original broadcast</Link>:
          texts, destination and category were copied, the audience was not. Set the audience, then count it.
        </Alert>
      ) : null}
      {lockReason ? <Alert variant="info">{lockReason}</Alert> : null}
      <PushIssueList issues={issues} title="The server refused the change" />
      {broadcast && broadcast.warnings.length > 0 ? (
        <PushIssueList
          issues={broadcast.warnings}
          title="Saved texts likely cut on a lock screen (not blocking)"
          variant="warning"
        />
      ) : null}

      {broadcast && isStarted ? <PushProgress broadcast={broadcast} onChanged={setBroadcast} /> : null}

      <Row>
        <Col lg={7}>
          <PushBroadcastEditor
            draft={shown}
            onChange={setDraft}
            options={options}
            savedDestination={broadcast?.destination ?? null}
            disabled={!!lockReason || busy}
            lockedReason={lockReason}
            translationsSpreadsheet={
              broadcast ? (
                <PushTranslationsXlsx
                  broadcast={broadcast}
                  languages={options.pushBroadcastLanguages}
                  blockedReason={
                    lockReason
                      ? 'Only a draft takes a spreadsheet import.'
                      : isDirty
                      ? 'Save your edits first: the template and the import work on the saved texts.'
                      : null
                  }
                  onApplied={applySaved}
                />
              ) : null
            }
          />
          <PushAudience
            draft={shown}
            onChange={setDraft}
            options={options}
            broadcast={broadcast}
            people={people}
            disabled={!!lockReason || busy}
            lockedReason={lockReason}
            isDirty={isDirty}
            isFiltersDirty={isFiltersDirty}
            isContentDirty={isContentDirty}
            counting={counting}
            onCount={count}
            sendSection={
              broadcast?.status === PushBroadcastStatus.DRAFT ? (
                <PushSendPanel
                  broadcast={broadcast}
                  options={options}
                  people={people}
                  isDirty={isDirty}
                  onStarted={applySaved}
                />
              ) : null
            }
          />
        </Col>
        <Col lg={5}>
          {/* Beside the editor, kept in view while scrolling: the only place a player's push is seen before a send. */}
          <div style={{ position: 'sticky', top: '1rem', maxHeight: 'calc(100vh - 2rem)', overflowY: 'auto' }}>
            {broadcast?.status === PushBroadcastStatus.SCHEDULED ? (
              <PushSchedulePanel broadcast={broadcast} people={people} onChanged={applySaved} />
            ) : null}
            <PushPreview
              broadcastId={broadcast?.id ?? null}
              titleTexts={shown.titleTexts}
              bodyTexts={shown.bodyTexts}
              languages={options.pushBroadcastLanguages}
              sourceLanguage={sourceLanguage}
              overrideLanguages={pushOverrideLanguages(shown, sourceLanguage, options.pushBroadcastLanguages)}
            />
          </div>
        </Col>
      </Row>
    </>
  );
}
